// The manifest contract, shared by the app that reads a manifest and the release
// script that writes one.
//
// One file because two things need it and they must never drift: a signing input
// that differs by a single character makes every device reject every update,
// silently. It cannot live in composables/useOtaUpdate.js, because that module
// imports Capacitor and a Node script cannot load it — so keep this file free of
// imports and both sides can use it as-is.

const REQUIRED_MANIFEST_FIELDS = ['bundleId', 'url', 'checksum', 'signature', 'metaSignature', 'buildDate', 'targetNativeVersion'];

// Production is the unnamed default: it lives in `ota-manifest.json` with no
// suffix, which is why it must never be passed around as a channel name.
export const DEFAULT_CHANNEL = 'production';

// A channel is a filename fragment on one side (`ota-manifest-beta.json`) and a
// localStorage value on the other, so both ends have to agree on what a legal
// name looks like — the release script must not publish a channel the app would
// refuse to ask for, and the app must not build a URL out of whatever some other
// script on the origin left in localStorage.
export const isChannelName = (value) => typeof value === 'string' && /^[a-z][a-z0-9-]*$/.test(value);

// The native plugin verifies the zip's bytes, which stops anyone forging a new
// payload — but it never sees the manifest, and `buildDate` is what decides
// whether to update at all. Signing the metadata too closes the gap where an
// attacker who can rewrite the manifest replays an older, genuinely signed
// bundle under a forged date. `mandatory` is in here for the same reason: it
// drives an uncloseable restart dialog, so flipping it must break the signature.
// Normalised to a boolean rather than passed through, so a manifest that omits
// the field signs identically to one that spells out `false`.
//
// `signature` is in here despite looking redundant. It is the only field the
// device actually checks the payload against: the plugin verifies either the
// signature or the checksum, never both, and it always has a public key — so the
// signed `checksum` never runs on iOS. Left unsigned, anyone able to rewrite the
// manifest could corrupt `signature` and wedge updates for good, or pair the
// signed `url` with a different release's genuine signature.
export const getManifestSigningInput = (manifest) => [
  manifest.bundleId,
  manifest.buildDate,
  manifest.targetNativeVersion,
  manifest.checksum,
  manifest.url,
  String(manifest.mandatory === true),
  manifest.signature,
].join('\n');

// Part of the same contract: both sides check the signature above against the
// same committed key, so both have to read that key the same way. They cannot
// share the verification itself — Web Crypto in the app, `node:crypto` in the
// release script — but both need the SPKI bytes rather than the PEM wrapper, and
// two hand-rolled unwrappings are exactly how the two ends drift apart.
//
// Whitespace goes too, so the wrapped PEM `openssl` prints reads the same as the
// single-line one `bun ota:keygen` writes.
export const getPemBody = (pem) => pem.replace(/-----[A-Z ]+-----/g, '').replace(/\s/g, '');

// A wrong/missing path on the site returns the SPA shell with HTTP 200, so a
// successful request proves nothing — the payload has to be checked explicitly.
export const isManifestValid = (manifest) => {
  if (!manifest || typeof manifest !== 'object' || Array.isArray(manifest)) {
    return false;
  }

  return REQUIRED_MANIFEST_FIELDS.every((field) => typeof manifest[field] === 'string' && manifest[field].length > 0);
};
